import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthLayout, FormError } from '../../components/Form.js';
import { ApiError, apiRequest } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';

/**
 * Shown in place of the console when the signed-in account has not accepted
 * the current documents.
 *
 * `returning` distinguishes a version bump from a first sight: someone who
 * agreed to something before is owed an explanation of why they are being
 * asked again.
 */
export function AcceptTerms({ returning }: { returning: boolean }) {
  const { refreshMe, signOut } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function accept() {
    setError(null);
    setBusy(true);

    try {
      await apiRequest('/me/accept-terms', { method: 'POST' });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not record your acceptance.');
      setBusy(false);
      return;
    }

    // Kept separate from the call above, because failing here does not mean
    // the acceptance failed — it is already recorded. Saying otherwise would
    // be a lie in the one window where it matters. Recording twice is
    // harmless, so trying again is always safe.
    try {
      // Succeeds into an unmount — refreshMe drops the gate — so `busy` is
      // only ever cleared on a failure.
      await refreshMe();
    } catch {
      setError('Your acceptance was saved, but the page could not reload. Try again.');
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title={returning ? 'We have updated our Terms' : 'Before you start'}
      subtitle={
        returning
          ? 'Please review the updated documents and accept them to carry on.'
          : 'Please review these and accept them to carry on.'
      }
    >
      <p className="muted">
        Read the{' '}
        <Link to="/terms" target="_blank" rel="noreferrer">
          Terms of Service
        </Link>{' '}
        and the{' '}
        <Link to="/privacy" target="_blank" rel="noreferrer">
          Privacy Policy
        </Link>
        . Both open in a new tab so you do not lose your place.
      </p>

      <FormError message={error} />

      <button type="button" className="block" disabled={busy} onClick={() => void accept()}>
        {busy ? 'Saving…' : 'I agree'}
      </button>

      <button type="button" className="secondary block" onClick={signOut}>
        Sign out
      </button>
    </AuthLayout>
  );
}
